import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ModernButton } from '../../components/ui/ModernButton';
import { CustomerService } from '../../services/customerService';
import { ActivityService } from '../../services/activityService';
import { useAuth } from '../../contexts/AuthContext';
import { Customer, CustomerInsight } from '../../types/agent';
import './CustomerDetailPage.css';

export const CustomerDetailPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { currentAgent } = useAuth();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [insights, setInsights] = useState<CustomerInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);

  const loadCustomer = useCallback(async () => {
    if (!customerId) return;
    
    setLoading(true);
    try {
      const data = await CustomerService.getCustomer(customerId);
      setCustomer(data);
      
      // Log activity when customer is viewed
      if (data && currentAgent?.id) {
        const customerName = `${data.firstName} ${data.lastName}`.trim();
        await ActivityService.logActivity(
          currentAgent.id,
          'customer_viewed',
          'customer',
          customerId,
          customerName,
          {
            customerEmail: data.email
          }
        );
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load customer details',
        life: 3000
      });
      // Navigate back to customers list after a delay
      setTimeout(() => navigate('/agent/customers'), 2000);
    } finally {
      setLoading(false);
    }
  }, [customerId, navigate, toast, currentAgent]);

  const loadInsights = useCallback(async () => {
    if (!customerId) return;

    setInsightsLoading(true);
    try {
      // Passes the customer document id; the server resolves the underlying
      // user id and applies the customer's sharing consent before returning
      // anything.
      const data = await CustomerService.getCustomerInsights(customerId);
      setInsights(data);
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  useEffect(() => {
    // Load insights after customer is loaded
    if (customer) {
      loadInsights();
    }
  }, [customer, loadInsights]);

  const handleEdit = () => {
    navigate(`/agent/customers/${customerId}/edit`);
  };

  const handleDelete = () => {
    confirmDialog({
      message: `Are you sure you want to delete ${customer?.firstName} ${customer?.lastName}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await CustomerService.deleteCustomer(customerId!);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Customer deleted successfully',
            life: 3000
          });
          setTimeout(() => navigate('/agent/customers'), 1500);
        } catch (error) {
          console.error('Error deleting customer:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete customer',
            life: 3000
          });
        }
      }
    });
  };

  const handleCall = () => {
    if (customer?.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
  };

  const handleEmail = () => {
    if (customer?.email) {
      window.location.href = `mailto:${customer.email}`;
    }
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'prospect': return 'info';
      case 'inactive': return 'warning';
      case 'converted': return 'success';
      default: return 'info';
    }
  };

  const getPrioritySeverity = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'info';
    }
  };

  const formatDate = (date: Date | undefined | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="customer-detail-page">
        <div className="loading-container">
          <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem' }}></i>
          <p>Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="customer-detail-page">
        <Toast ref={toast} />
        <Card>
          <div className="error-container">
            <i className="pi pi-exclamation-circle" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
            <h2>Customer Not Found</h2>
            <p>The requested customer could not be found.</p>
            <ModernButton
              variant="primary"
              icon="pi pi-arrow-left"
              onClick={() => navigate('/agent/customers')}
            >
              Back to Customers
            </ModernButton>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="customer-detail-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Header */}
      <div className="page-header">
        <div className="header-top-row">
          <h1 className="customer-name">{customer.firstName} {customer.lastName}</h1>
          <Tag
            value={customer.status.toUpperCase()}
            severity={getStatusSeverity(customer.status)}
            className="status-tag"
          />
        </div>
        <div className="header-info-row">
          <span className="customer-id">ID: {customerId}</span>
          <span className="member-since">Member since {formatDate(customer.createdAt)}</span>
        </div>
        <div className="header-actions-row">
          <div className="header-actions-left">
            <ModernButton
              variant="ghost"
              icon="pi pi-arrow-left"
              onClick={() => navigate('/agent/customers')}
            >
              Back to List
            </ModernButton>
          </div>
          <div className="header-actions-right">
            <ModernButton
              variant="outline"
              icon="pi pi-phone"
              onClick={handleCall}
              disabled={!customer.phone}
            >
              Call
            </ModernButton>
            <ModernButton
              variant="outline"
              icon="pi pi-envelope"
              onClick={handleEmail}
            >
              Email
            </ModernButton>
            <ModernButton
              variant="primary"
              icon="pi pi-pencil"
              onClick={handleEdit}
            >
              Edit
            </ModernButton>
            <ModernButton
              variant="danger"
              icon="pi pi-trash"
              onClick={handleDelete}
            >
              Delete
            </ModernButton>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon lead-score-icon">
              <i className="pi pi-chart-line"></i>
            </div>
            <div className="stat-details">
              <span className="stat-label">Lead Score</span>
              <span className="stat-value">{customer.leadScore || 0}</span>
            </div>
          </div>
          <div className="stat-progress">
            <div 
              className="stat-progress-bar" 
              style={{ width: `${customer.leadScore || 0}%` }}
            />
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon interactions-icon">
              <i className="pi pi-comments"></i>
            </div>
            <div className="stat-details">
              <span className="stat-label">Total Interactions</span>
              <span className="stat-value">{customer.totalInteractions || 0}</span>
            </div>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon insights-icon">
              <i className="pi pi-lightbulb"></i>
            </div>
            <div className="stat-details">
              <span className="stat-label">Active Insights</span>
              <span className="stat-value">{insights.filter(i => i.status === 'new' || i.status === 'in_progress' || i.status === 'viewed' || i.status === 'contacted').length}</span>
            </div>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-content">
            <div className="stat-icon source-icon">
              <i className="pi pi-tag"></i>
            </div>
            <div className="stat-details">
              <span className="stat-label">Source</span>
              <span className="stat-value source-value">
                {customer.source ? customer.source.charAt(0).toUpperCase() + customer.source.slice(1) : 'Direct'}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Customer Information Grid */}
      <div className="detail-grid">
        {/* Contact Information */}
        <Card className="detail-section">
          <div className="section-header">
            <i className="pi pi-id-card"></i>
            <h3>Contact Information</h3>
          </div>
          <div className="info-list">
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-envelope"></i>
              </div>
              <div className="info-content">
                <label>Email Address</label>
                <p>{customer.email}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-phone"></i>
              </div>
              <div className="info-content">
                <label>Phone Number</label>
                <p>{customer.phone || 'Not provided'}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-map-marker"></i>
              </div>
              <div className="info-content">
                <label>Location</label>
                <p>{customer.profileData?.location || 'Not provided'}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-calendar"></i>
              </div>
              <div className="info-content">
                <label>Customer Since</label>
                <p>{formatDate(customer.createdAt)}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Lead & Engagement */}
        <Card className="detail-section">
          <div className="section-header">
            <i className="pi pi-chart-bar"></i>
            <h3>Lead & Engagement</h3>
          </div>
          <div className="info-list">
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-flag"></i>
              </div>
              <div className="info-content">
                <label>Status</label>
                <p>
                  <Tag
                    value={customer.status.toUpperCase()}
                    severity={getStatusSeverity(customer.status)}
                  />
                </p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-star"></i>
              </div>
              <div className="info-content">
                <label>Lead Score</label>
                <p>
                  <div className="lead-score-display">
                    <span className="score-value">{customer.leadScore || 0}</span>
                    <span className="score-label">/ 100</span>
                  </div>
                </p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-inbox"></i>
              </div>
              <div className="info-content">
                <label>Lead Source</label>
                <p>{customer.source ? customer.source.charAt(0).toUpperCase() + customer.source.slice(1) : 'Direct'}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-users"></i>
              </div>
              <div className="info-content">
                <label>Total Interactions</label>
                <p>{customer.totalInteractions || 0} interactions</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Assigned Agent */}
        <Card className="detail-section">
          <div className="section-header">
            <i className="pi pi-user-edit"></i>
            <h3>Assignment Information</h3>
          </div>
          <div className="info-list">
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-user"></i>
              </div>
              <div className="info-content">
                <label>Assigned Agent</label>
                <p>{customer.assignedAgentId ? currentAgent?.id === customer.assignedAgentId ? 'You' : 'Another Agent' : 'Unassigned'}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-clock"></i>
              </div>
              <div className="info-content">
                <label>Last Updated</label>
                <p>{formatDate(customer.updatedAt)}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-calendar-plus"></i>
              </div>
              <div className="info-content">
                <label>Last Contact</label>
                <p>{customer.lastContactAt ? formatDate(customer.lastContactAt) : 'No contact yet'}</p>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <i className="pi pi-chart-pie"></i>
              </div>
              <div className="info-content">
                <label>Last Activity</label>
                <p>{customer.lastActivityAt ? formatDate(customer.lastActivityAt) : 'No recent activity'}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tags */}
      {customer.tags && customer.tags.length > 0 && (
        <Card className="detail-section full-width">
          <div className="section-header">
            <i className="pi pi-tags"></i>
            <h3>Tags</h3>
          </div>
          <div className="tags-container">
            {customer.tags.map((tag, index) => (
              <Tag key={index} value={tag} className="customer-tag" />
            ))}
          </div>
        </Card>
      )}

      {/* Notes */}
      {customer.notes && (
        <Card className="detail-section full-width">
          <div className="section-header">
            <i className="pi pi-file-edit"></i>
            <h3>Notes</h3>
          </div>
          <p className="notes-content">{customer.notes}</p>
        </Card>
      )}

      {/* Customer Insights */}
      <Card className="detail-section full-width insights-section">
        <div className="section-header">
          <i className="pi pi-lightbulb"></i>
          <h3>Recent Insights</h3>
          <span className="insights-count">
            {insights.length} {insights.length === 1 ? 'insight' : 'insights'}
          </span>
        </div>
        {insightsLoading ? (
          <div className="loading-container">
            <i className="pi pi-spinner pi-spin"></i>
            <p>Loading insights...</p>
          </div>
        ) : insights.length > 0 ? (
          <DataTable 
            value={insights} 
            responsiveLayout="scroll"
            stripedRows
            paginator
            rows={10}
            className="insights-table"
          >
            <Column 
              field="type" 
              header="Type" 
              body={(rowData) => {
                const type = rowData.type ? String(rowData.type) : 'unknown';
                return (
                  <div className="insight-type">
                    <i className="pi pi-circle-fill"></i>
                    <span style={{ textTransform: 'capitalize' }}>
                      {type.replace(/_/g, ' ')}
                    </span>
                  </div>
                );
              }} 
            />
            <Column 
              field="priority" 
              header="Priority" 
              body={(rowData) => {
                const priority = rowData.priority ? String(rowData.priority).toLowerCase() : 'medium';
                return (
                  <Tag
                    value={priority.toUpperCase()}
                    severity={getPrioritySeverity(priority)}
                  />
                );
              }} 
            />
            <Column field="title" header="Title" style={{ minWidth: '250px' }} />
            <Column 
              field="status" 
              header="Status" 
              body={(rowData) => {
                const status = rowData.status ? String(rowData.status) : 'unknown';
                return <Tag value={status.toUpperCase()} />;
              }} 
            />
            <Column 
              field="createdAt" 
              header="Created" 
              body={(rowData) => formatDate(rowData.createdAt)} 
            />
          </DataTable>
        ) : (
          <div className="empty-state">
            <i className="pi pi-inbox"></i>
            <h4>No insights available</h4>
            <p>There are no insights generated for this customer yet.</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CustomerDetailPage;

